/**
 * Helpers for the one-tap "Log" sheet. Pure functions so they are easy to test.
 */

/** Adds `days` to today and returns an ISO date (YYYY-MM-DD) in local time. */
export const addDays = (days: number, from: Date = new Date()): string => {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

/** Next-step date chips shown under the note box. */
export const NEXT_STEP_CHIPS: { label: string; days: number }[] = [
  { label: 'Today', days: 0 },
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
];

/**
 * One-tap outcome phrases per activity type (matched on lower-cased type
 * name). Tapping one appends a line to the note so reps can log a call in two
 * taps and only type what is genuinely new.
 */
const OUTCOMES: Record<string, string[]> = {
  call: [
    'No answer',
    'Left voicemail',
    'Asked to call back',
    'Interested',
    'Not interested',
    'Meeting set',
    'Sent details',
    'Wrong number',
  ],
  whatsapp: ['Sent intro', 'Sent details', 'Replied – interested', 'No reply yet', 'Meeting set'],
  email: ['Sent intro', 'Sent proposal', 'Sent follow-up', 'Replied – interested', 'Bounced'],
  meeting: [
    'Went well',
    'Needs proposal',
    'Needs follow-up',
    'Decision maker not present',
    'Budget concerns',
    'Timing not right',
  ],
  demo: ['Impressed', 'Needs proposal', 'Technical questions', 'Objections raised', 'No-show'],
  note: [],
};

const GENERIC_OUTCOMES = ['Positive', 'Needs follow-up', 'No response', 'Not now'];

export const outcomesFor = (typeName: string | undefined): string[] => {
  const key = (typeName ?? '').trim().toLowerCase();
  if (key in OUTCOMES) return OUTCOMES[key]!;
  return GENERIC_OUTCOMES;
};

/** Toggles an outcome line in the summary: adds it if absent, removes it if present. */
export const toggleOutcome = (summary: string, outcome: string): string => {
  const lines = summary.split('\n');
  const idx = lines.findIndex((l) => l.trim() === outcome);
  if (idx >= 0) {
    lines.splice(idx, 1);
    return lines.join('\n').replace(/^\n+/, '');
  }
  const base = summary.replace(/\s+$/, '');
  return base ? `${base}\n${outcome}` : outcome;
};

export const hasOutcome = (summary: string, outcome: string) =>
  summary.split('\n').some((l) => l.trim() === outcome);

/** Appends dictated text to the note with sensible spacing. */
export const appendDictation = (summary: string, text: string): string => {
  const t = text.trim();
  if (!t) return summary;
  if (!summary.trim()) return t;
  const needsSpace = !/\s$/.test(summary);
  return `${summary}${needsSpace ? ' ' : ''}${t}`;
};

export const fmtSeconds = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
